import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useI18n } from "../../i18n/LanguageContext";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Download, RotateCcw, ShieldOff } from "lucide-react";
import { toast } from "sonner";

export default function SuperAdminCertificates() {
  const { t } = useI18n();
  const [rows, setRows] = useState(null);

  const load = () => { setRows(null); api.saListCerts().then(setRows).catch((e) => { toast.error(e.message); setRows([]); }); };
  useEffect(() => { load(); }, []);

  const revoke = async (c) => {
    if (!window.confirm(t("superadmin.confirmRevoke"))) return;
    try { await api.saRevokeCert(c.certificate_id); toast.success("✓"); load(); }
    catch (e) { toast.error(e.message); }
  };
  const reissue = async (c) => {
    try { await api.saReissueCert(c.certificate_id); toast.success("✓"); load(); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-6" data-testid="sa-certs-page">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-[#F72585]">{t("superadmin.title")}</div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tight">{t("superadmin.certificates")}</h1>
        </div>
        <a href={api.saExportUrl("certificates")} target="_blank" rel="noreferrer"
           className="inline-flex items-center gap-2 rounded-full bg-white border border-black/10 px-4 py-2 text-sm font-bold hover:bg-black/5"
           data-testid="sa-export-certs-link">
          <Download className="h-4 w-4" /> {t("superadmin.exportCerts")}
        </a>
      </header>

      <div className="rounded-2xl border border-black/5 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-black/5">
            <tr>
              <Th>certificate_id</Th><Th>student</Th><Th>course</Th><Th>score</Th>
              <Th>issued</Th><Th>status</Th><Th>{t("superadmin.actions")}</Th>
            </tr>
          </thead>
          <tbody>
            {rows === null && <tr><td colSpan={7} className="p-6 text-center text-black/50">{t("superadmin.loading")}</td></tr>}
            {rows?.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-black/50">{t("superadmin.empty")}</td></tr>}
            {rows?.map((c) => (
              <tr key={c.certificate_id} className="border-t border-black/5" data-testid={`sa-cert-${c.certificate_id}`}>
                <Td className="font-mono text-xs">{c.certificate_id}</Td>
                <Td className="text-sm font-semibold">{c.student_name}</Td>
                <Td className="text-xs">{c.course_title_en}</Td>
                <Td className="font-black text-[#4361EE]">{c.score_percent}%</Td>
                <Td className="text-xs text-black/60">{c.issued_at?.slice(0, 10)}</Td>
                <Td>
                  {c.revoked ? (
                    <Badge className="bg-[#D90429]/20 text-[#D90429] text-[10px]">revoked</Badge>
                  ) : (
                    <Badge className="bg-[#4CC9F0]/20 text-[#0A5566] text-[10px]">active</Badge>
                  )}
                </Td>
                <Td>
                  <div className="flex gap-1.5">
                    {c.revoked ? (
                      <Button size="sm" onClick={() => reissue(c)}
                              className="rounded-full bg-[#4CC9F0]/20 text-[#0A5566] text-xs font-bold h-7 hover:bg-[#4CC9F0]/30"
                              data-testid={`sa-reissue-${c.certificate_id}`}>
                        <RotateCcw className="h-3 w-3 me-1" /> {t("superadmin.reissue")}
                      </Button>
                    ) : (
                      <Button size="sm" onClick={() => revoke(c)}
                              className="rounded-full bg-[#D90429]/10 text-[#D90429] text-xs font-bold h-7 hover:bg-[#D90429]/20"
                              data-testid={`sa-revoke-${c.certificate_id}`}>
                        <ShieldOff className="h-3 w-3 me-1" /> {t("superadmin.revoke")}
                      </Button>
                    )}
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Th({ children }) { return <th className="text-start px-3 py-2.5 text-[11px] font-black uppercase tracking-wider text-black/60">{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-3 py-2.5 ${className}`}>{children}</td>; }
